import React, { ReactNode, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { some } from 'lodash';
import moment from 'moment';

import { useScreenSizeDetection } from 'common/hooks';
import { ProductTypes } from 'consts/productTypes/productTypes';
import { isAdriagate } from 'modules/_common';
import { useAuthState } from 'services/auth/AuthContext';

export const LOGIN = '/login';
export const DASHBOARD = '/accommodations/dashboard';

export const useAuthRoutes = () => {
  const {
    state: { isRegistrationPending, email, isAuthenticated },
  } = useAuthState();

  const { boot, update } = useIntercom();
  const { isMobile } = useScreenSizeDetection();

  const location = useLocation();
  const navigate = useNavigate();
  const firstPath = location.pathname.split('/')[1];

  useEffect(() => {
    update({ hideDefaultLauncher: isMobile });
  }, [isMobile, update]);

  const checkPublic = (element: ReactNode): ReactNode =>
    isAuthenticated ? <Navigate to={DASHBOARD} replace /> : element;

  const checkRegistration = (element: ReactNode): ReactNode =>
    isRegistrationPending ? element : <Navigate to={LOGIN} replace />;

  const checkPrivate = (element: ReactNode, profileInfo: any): ReactNode => {
    const isPlanActive =
      !profileInfo?.licence?.isTrial && moment().isBefore(moment(profileInfo?.licence?.validTo));
    const isTrialPlanActive =
      profileInfo?.licence?.isTrial &&
      moment().isBefore(moment(profileInfo?.licence?.trailValidTo));
    const isAnyPlanActive = isPlanActive || isTrialPlanActive;

    const rentalsSubscriptionActive = some(profileInfo?.products, { code: ProductTypes.SMJ });
    const toursSubscriptionActive = some(profileInfo?.products, { code: ProductTypes.IZL });

    const hasAnyProperty = profileInfo?.hasObjects;
    const isRenter = profileInfo?.userType === 'renter';

    // adriagate intercom za lite korisnike
    if (isAuthenticated && isAdriagate && isRenter && !isMobile) {
      boot({
        name: `${profileInfo?.firstName} ${profileInfo?.lastName}`,
        email: profileInfo?.email,
        alignment: 'left',
        verticalPadding: 60,
        customAttributes: {
          project: 'rentistic',
        },
      });
    }

    if (isAuthenticated) {
      // If user has no active plan
      if (!isAnyPlanActive) {
        firstPath !== 'settings' && navigate('/settings/subscription');

        return element;
      }

      // if lite user has no property
      else if (
        isRenter &&
        !hasAnyProperty &&
        firstPath !== 'settings' &&
        !location.pathname.startsWith('/accommodations/rentals')
      ) {
        navigate('/accommodations/rentals');

        return element;
      }

      // If user has any plan active
      // Check if accommodations are active and if not Navigate to tours
      else if (firstPath === 'accommodations' && !rentalsSubscriptionActive) {
        return <Navigate to="/tours" />;
      }

      // Check if tours are active and if not Navigate to accommodations
      else if (firstPath === 'tours' && !toursSubscriptionActive) {
        return <Navigate to="/accommodations" />;
      }

      // Default Navigate will be /accommodations or /tours depending whitch package is active
      else if (firstPath === '') {
        if (rentalsSubscriptionActive) {
          return <Navigate to="/accommodations" />;
        } else {
          return <Navigate to="/tours" />;
        }
      } else {
        return element;
      }
    } else {
      return <Navigate to={LOGIN} replace />;
    }
  };

  return {
    isAuthenticated,
    isRegistrationPending,
    email,
    checkPublic,
    checkRegistration,
    checkPrivate,
  };
};
